import type { EnvelopeRow, Friend, FriendDevice } from '../types';
import type {
  OneTimePrekeyPublic,
  PrekeyBundlePublic,
} from './signal';
import { getDb, hasSupabaseConfig } from './db';

export { hasSupabaseConfig };

// ---------------------------------------------------------------------------
// Identity (one row per user)
// ---------------------------------------------------------------------------

// Identity rows are only created through publishIdentityForUser in
// identity.ts (which enforces auth.uid = identities.id under Phase 1 RLS).
// There's no standalone "create identity" path on the relay because that
// would never work for callers who aren't the row's owner anyway.

/**
 * Look up a user by username and fetch all of their currently-registered
 * devices in one shot. Returns null if no such user exists.
 */
export async function lookupByUsername(username: string): Promise<Friend | null> {
  const db = getDb();

  const { data: row, error } = await db
    .from('identities')
    .select('id, username')
    .eq('username', username)
    .maybeSingle();
  if (error) throw error;
  if (!row) return null;

  const devices = await fetchUserDevices(row.id);
  return { id: row.id, username: row.username, devices };
}

/**
 * Fetch all devices for a given user. Used by the sender to know who to
 * fan out to, and by the boot-time backfill to refresh stale friend
 * records.
 */
export async function fetchUserDevices(userId: string): Promise<FriendDevice[]> {
  const { data, error } = await getDb()
    .from('devices')
    .select('id, label, outer_pubkey_b64, signal_identity_pubkey_b64')
    .eq('user_id', userId);
  if (error) throw error;
  return (data ?? []) as FriendDevice[];
}

/**
 * Used to look up the local user's own devices (for the "linked devices"
 * Settings list). Same data shape; separate name to make intent clear at
 * call sites.
 */
export async function fetchOwnDevices(userId: string): Promise<FriendDevice[]> {
  return fetchUserDevices(userId);
}

// ---------------------------------------------------------------------------
// Envelope mailbox (now routed per device)
// ---------------------------------------------------------------------------

export async function postEnvelope(args: {
  recipient_device_id: string;
  ciphertext_b64: string;
  deliver_at?: string; // ISO timestamp; omit for immediate delivery
}): Promise<void> {
  const { error } = await getDb().from('envelopes').insert({
    recipient_device_id: args.recipient_device_id,
    ciphertext_b64: args.ciphertext_b64,
    size_bytes: args.ciphertext_b64.length,
    ...(args.deliver_at ? { deliver_at: args.deliver_at } : {}),
  });
  if (error) {
    // 23503 = foreign_key_violation. envelopes.recipient_device_id
    // references devices(id); a missing row means the recipient's
    // device is gone (unlinked, or the user wiped). Friendly message.
    if (error.code === '23503') {
      throw new Error(
        "That device isn't on No Rush anymore — it may have been unlinked or wiped.",
      );
    }
    throw error;
  }
}

export async function fetchMyEnvelopes(my_device_id: string): Promise<EnvelopeRow[]> {
  const { data, error } = await getDb()
    .from('envelopes')
    .select('id, ciphertext_b64, size_bytes, created_at')
    .eq('recipient_device_id', my_device_id)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []) as EnvelopeRow[];
}

export async function deleteEnvelopes(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await getDb().from('envelopes').delete().in('id', ids);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Username management
// ---------------------------------------------------------------------------

export async function renameUsername(
  userId: string,
  newUsername: string,
): Promise<void> {
  const { error } = await getDb()
    .from('identities')
    .update({ username: newUsername })
    .eq('id', userId);
  if (error) {
    if (error.code === '23505') {
      throw new Error(
        `Username "${newUsername}" is taken. Pick a different one.`,
      );
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Device + prekey bundle publishing
// ---------------------------------------------------------------------------

export interface PublishDeviceArgs {
  user_id: string;
  label: string;
  outer_pubkey_b64: string;
  bundle: PrekeyBundlePublic;
  one_time_prekeys: OneTimePrekeyPublic[];
}

/**
 * Register a new device for the authenticated user and publish its full
 * bundle (identity key, signed prekey, signature, and an initial batch of
 * one-time prekeys). Returns the assigned device_id.
 */
export async function publishDevice(args: PublishDeviceArgs): Promise<string> {
  const db = getDb();

  const { data: deviceRow, error: devErr } = await db
    .from('devices')
    .insert({
      user_id: args.user_id,
      label: args.label,
      outer_pubkey_b64: args.outer_pubkey_b64,
      signal_registration_id: args.bundle.registration_id,
      signal_identity_pubkey_b64: args.bundle.identity_pubkey_b64,
      signed_prekey_id: args.bundle.signed_prekey_id,
      signed_prekey_pubkey_b64: args.bundle.signed_prekey_pubkey_b64,
      signed_prekey_signature_b64: args.bundle.signed_prekey_signature_b64,
      last_seen_at: new Date().toISOString(),
    })
    .select('id')
    .single();
  if (devErr) throw devErr;

  const deviceId = deviceRow.id as string;

  if (args.one_time_prekeys.length > 0) {
    const rows = args.one_time_prekeys.map((p) => ({
      device_id: deviceId,
      key_id: p.key_id,
      pubkey_b64: p.pubkey_b64,
    }));
    const { error: opkErr } = await db.from('one_time_prekeys').insert(rows);
    if (opkErr) throw opkErr;
  }

  return deviceId;
}

/**
 * Delete a device — unlinks it from the user. Cascades to envelopes
 * addressed to that device and to its one-time prekeys.
 */
export async function deleteDevice(deviceId: string): Promise<void> {
  const { error } = await getDb()
    .from('devices')
    .delete()
    .eq('id', deviceId);
  if (error) throw error;
}

/**
 * Fetch a single device's bundle (with one one-time prekey atomically
 * consumed) for X3DH session start. Returns null when the device row
 * doesn't exist anymore (unlinked between the previous lookup and now).
 *
 * If the recipient has run out of one-time prekeys, one_time_prekey is
 * undefined; X3DH still works at slightly weaker forward secrecy for the
 * very first message.
 */
export interface FetchedDeviceBundle {
  bundle: PrekeyBundlePublic & { device_id: string };
  one_time_prekey?: OneTimePrekeyPublic;
}

export async function fetchDeviceBundle(
  deviceId: string,
): Promise<FetchedDeviceBundle | null> {
  const db = getDb();
  const { data: row, error: devErr } = await db
    .from('devices')
    .select(
      'id, user_id, signal_registration_id, signal_identity_pubkey_b64, signed_prekey_id, signed_prekey_pubkey_b64, signed_prekey_signature_b64',
    )
    .eq('id', deviceId)
    .maybeSingle();
  if (devErr) throw devErr;
  if (!row) return null;

  const { data: opkRows, error: opkErr } = await db.rpc(
    'consume_one_time_prekey',
    { target_device_id: deviceId },
  );
  if (opkErr) throw opkErr;
  const opk = Array.isArray(opkRows) && opkRows.length > 0 ? opkRows[0] : null;

  return {
    bundle: {
      device_id: row.id,
      registration_id: row.signal_registration_id,
      identity_pubkey_b64: row.signal_identity_pubkey_b64,
      signed_prekey_id: row.signed_prekey_id,
      signed_prekey_pubkey_b64: row.signed_prekey_pubkey_b64,
      signed_prekey_signature_b64: row.signed_prekey_signature_b64,
    },
    one_time_prekey: opk
      ? { key_id: opk.key_id, pubkey_b64: opk.pubkey_b64 }
      : undefined,
  };
}

/** Count own one-time prekeys for the given device. Used by replenisher. */
export async function countOwnOneTimePrekeys(deviceId: string): Promise<number> {
  const { count, error } = await getDb()
    .from('one_time_prekeys')
    .select('*', { count: 'exact', head: true })
    .eq('device_id', deviceId);
  if (error) throw error;
  return count ?? 0;
}

/** Highest existing one-time prekey id, so the replenisher can resume IDs. */
export async function maxOneTimePrekeyId(deviceId: string): Promise<number> {
  const { data, error } = await getDb()
    .from('one_time_prekeys')
    .select('key_id')
    .eq('device_id', deviceId)
    .order('key_id', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.key_id ?? 0;
}

/** Append more one-time prekeys to a device's pool. */
export async function publishMoreOneTimePrekeys(
  deviceId: string,
  opks: OneTimePrekeyPublic[],
): Promise<void> {
  if (opks.length === 0) return;
  const rows = opks.map((p) => ({
    device_id: deviceId,
    key_id: p.key_id,
    pubkey_b64: p.pubkey_b64,
  }));
  const { error } = await getDb()
    .from('one_time_prekeys')
    .upsert(rows, { onConflict: 'device_id,key_id', ignoreDuplicates: true });
  if (error) throw error;
}

/**
 * Touch the device's last_seen_at. Cheap; we run it after each poll cycle
 * so the user can see "this device · last active 2 minutes ago" in the
 * linked-devices list.
 */
export async function touchDeviceLastSeen(deviceId: string): Promise<void> {
  const { error } = await getDb()
    .from('devices')
    .update({ last_seen_at: new Date().toISOString() })
    .eq('id', deviceId);
  if (error) throw error;
}
